import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  DialogActions,
  FormControl,
  FormControlLabel,
  Checkbox,
  Box,
  TextField,
  Autocomplete,
  Typography,
  IconButton,
  Tab,
  Tabs,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Divider,
  CircularProgress,
  Chip,
  Avatar,
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import BuildIcon from '@mui/icons-material/Build';
import AddIcon from '@mui/icons-material/Add';
import BoltIcon from '@mui/icons-material/Bolt';
import { withTranslation } from 'react-i18next';
import HelpButton from '../shared/HelpButton';
import DecimalField from '../shared/DecimalField';
import axios from 'axios';
import { DEFAULT_VAT_RATE, calcPriceExclVat, formatPriceWithCurrency } from '../../utils/vat';
import { extractListFromResponse } from '../../utils/apiUtils';
import { useServerSearch } from '../../hooks/useServerSearch';
import { useUserStore } from '../../store';
import { durationMinutesToHours } from '../../utils/laborTime';

import { can } from '../../utils/access';
import VatModeToggle from '../shared/VatModeToggle';
import VatConverseLine from '../shared/VatConverseLine';
const priceTypeOptions = [
  { value: 'fixed', label: 'Fixed price' },
  { value: 'hourly', label: 'Hourly rate' },
  { value: 'variable', label: 'Variable' },
];

function TabPanel({ children, value, index, ...props }) {
  return (
    <Box role="tabpanel" hidden={value !== index} {...props} sx={{ pt: 2 }}>
      {value === index && children}
    </Box>
  );
}

function AddJobsDialog({ t, open, handleClose, setMaintenance, maintenance, mode = 'maintenance', onJobCreated }) {
  const isMaintenanceMode = mode === 'maintenance';
  // Template mode: service package editor. Pick OR create, callback with Job,
  // no maintenance POST.
  const isTemplateMode = mode === 'template';
  const { currentOrganization } = useUserStore();
  const orgDefaultVat = currentOrganization?.default_price_includes_vat ?? false;
  const canEditPrices = can(currentOrganization, 'work_orders.edit_prices');
  const canManageJobs = can(currentOrganization, 'parts_jobs.manage');

  // Server-side search for jobs catalog
  const {
    items: jobs,
    autocompleteProps: jobsAutocompleteProps,
    reset: resetJobsSearch,
  } = useServerSearch('/api/jobs/', { loadOnMount: false });

  const [categories, setCategories] = useState([]);
  // Job types the workshop pinned as quick selects (Job.is_quick_select), shown
  // as one-click chips above the search. null until loaded.
  const [quickJobs, setQuickJobs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [awaitingConfirmation, setAwaitingConfirmation] = useState(false);
  const [quantity, setQuantity] = useState('');
  // The quantity the last picked job prefilled, so picking another job can clear it again
  // without throwing away hours the user typed.
  const prefilledQuantity = useRef(null);
  const [existingJobDescription, setExistingJobDescription] = useState('');
  const [tab, setTab] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // New job form
  const [newJob, setNewJob] = useState({
    name: '',
    description: '',
    price: '',
    vat_rate: DEFAULT_VAT_RATE,
    job_code: '',
    category: null,
    estimated_duration: '',
    price_type: 'fixed',
  });
  const [priceIncludesVat, setPriceIncludesVat] = useState(orgDefaultVat);
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [allEmployees, setAllEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);

  const handleJobChange = (e, value) => {
    setSelectedJob(value);
    setExistingJobDescription('');

    // Hourly job: the hours start from the job's estimated duration (30 min -> 0,5 h), so
    // 92 EUR/h x 0,5 h bills 46 EUR instead of a full hour. Fixed-price jobs stay at 1: their
    // duration only feeds the labor time total. The field stays editable either way.
    const hours = value?.price_type === 'hourly' ? durationMinutesToHours(value.estimated_duration) : null;
    if (hours !== null) {
      prefilledQuantity.current = String(hours);
      setQuantity(String(hours));
    } else if (prefilledQuantity.current !== null && quantity === prefilledQuantity.current) {
      prefilledQuantity.current = null;
      setQuantity('');
    }
  };

  const addExistingJob = async () => {
    if (!selectedJob) return;
    setSubmitting(true);
    try {
      if (isTemplateMode) {
        if (onJobCreated) onJobCreated(selectedJob);
        setAwaitingConfirmation(false);
        handleClose();
        return;
      }
      const response = await axios.patch(`/api/maintenances/${maintenance.id}/add_job/`, {
        job_id: selectedJob.id,
        quantity: parseFloat(quantity) || 1,
        awaiting_confirmation: awaitingConfirmation,
        description: existingJobDescription,
        employee_ids: selectedEmployees.map(e => e.id),
      });
      setMaintenance(response.data);
      setAwaitingConfirmation(false);
      handleClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleNewJobChange = (field) => (e) => {
    setNewJob({ ...newJob, [field]: e.target.value });
  };

  const handleCategoryChange = (event, newValue) => {
    if (newValue && newValue.inputValue) {
      // User typed a new category name
      setShowNewCategory(true);
      setNewCategoryName(newValue.inputValue);
    } else {
      setNewJob((prev) => ({
        ...prev,
        category: newValue?.id || null,
      }));
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) return;

    try {
      const response = await axios.post('/api/job-categories/', {
        name: newCategoryName.trim(),
      });
      const newCategory = response.data;
      setCategories((prev) => [...prev, newCategory]);
      setNewJob((prev) => ({
        ...prev,
        category: newCategory.id,
      }));
      setShowNewCategory(false);
      setNewCategoryName('');
    } catch (err) {
      console.error('Error creating category:', err);
    }
  };

  const createAndAddJob = async () => {
    if (!newJob.name.trim()) {
      setError(t('Job name is required'));
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Get entered price and VAT rate (a lone "-" parses to NaN -> treat as 0)
      const parsedPrice = parseFloat(newJob.price);
      const enteredPrice = Number.isFinite(parsedPrice) ? parsedPrice : 0;
      const vatRate = parseFloat(newJob.vat_rate || 25.5);

      // Calculate VAT-exclusive price if needed
      let priceExclVat = enteredPrice;
      if (enteredPrice && priceIncludesVat) {
        priceExclVat = calcPriceExclVat(enteredPrice, vatRate);
      }

      // Create the new job with VAT preference
      const payload = {
        ...newJob,
        price: priceExclVat,
        vat_rate: vatRate,
        // Store VAT preference so it persists when editing
        stored_price_incl_vat: priceIncludesVat && enteredPrice ? Math.round(enteredPrice * 100) / 100 : null,
        price_entered_with_vat: priceIncludesVat && enteredPrice !== 0,
        estimated_duration: newJob.estimated_duration ? parseInt(newJob.estimated_duration, 10) : null,
        category: newJob.category || null,
      };

      const createResponse = await axios.post('/api/jobs/', payload);
      const createdJob = createResponse.data;

      if (isTemplateMode) {
        if (onJobCreated) onJobCreated(createdJob);
        handleClose();
        return;
      }

      // Add the newly created job to the maintenance
      const response = await axios.patch(`/api/maintenances/${maintenance.id}/add_job/`, {
        job_id: createdJob.id,
        quantity: parseFloat(quantity) || 1,
        awaiting_confirmation: awaitingConfirmation,
        employee_ids: selectedEmployees.map(e => e.id),
      });
      setMaintenance(response.data);
      handleClose();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const isNewJobValid = () => {
    return newJob.name?.trim();
  };

  // Fetch data and reset form when dialog opens
  useEffect(() => {
    if (open) {
      // Reset form state
      setTab(0);
      setSelectedJob(null);
      setAwaitingConfirmation(false);
      setQuantity('');
      prefilledQuantity.current = null;
      setExistingJobDescription('');
      setError(null);
      setNewJob({
        name: '',
        description: '',
        price: '',
        vat_rate: DEFAULT_VAT_RATE,
        job_code: '',
        category: null,
        estimated_duration: '',
        price_type: 'fixed',
      });
      setPriceIncludesVat(orgDefaultVat);
      setShowNewCategory(false);
      setNewCategoryName('');
      setSelectedEmployees([]);

      // Reset jobs search and load initial jobs
      resetJobsSearch();

      // Quick selects: active, own org, by name. A failure just hides the row.
      setQuickJobs(null);
      axios
        .get('/api/jobs/', { params: { is_quick_select: true, typeahead: true } })
        .then((res) => setQuickJobs(extractListFromResponse(res).data))
        .catch(() => setQuickJobs([]));

      // Fetch employees for assignment
      if (isMaintenanceMode) {
        axios.get('/api/employees/').then(res => {
          const data = res.data.results || res.data;
          setAllEmployees(Array.isArray(data) ? data : []);
        }).catch(() => {});
      }

      // Fetch categories
      const fetchCategories = async () => {
        setLoading(true);
        try {
          const response = await axios.get('/api/job-categories/');
          const { data } = extractListFromResponse(response);
          setCategories(data);
        } catch (err) {
          setError(err.message);
        } finally {
          setLoading(false);
        }
      };

      fetchCategories();
    }
  }, [open, resetJobsSearch]);

  const selectedCategory = categories.find((c) => c.id === newJob.category) || null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: '12px', sm: '16px' },
          m: { xs: 2, sm: 4 },
          maxHeight: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)' },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 3,
          pb: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: '20px',
            fontWeight: 600,
            color: 'text.primary',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}
        >
          {t('Add job')}
        </Typography>
        <IconButton onClick={handleClose} sx={{ color: 'text.disabled' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 2, sm: 3 }, pt: 1 }}>
        <Tabs
          value={tab}
          onChange={(e, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 500,
              textTransform: 'none',
              minWidth: { xs: 'auto', sm: 90 },
              px: { xs: 1.5, sm: 2 },
              fontSize: { xs: '13px', sm: '14px' },
            },
          }}
        >
          <Tab icon={<BuildIcon />} iconPosition="start" label={t('Select existing')} />
          <Tab icon={<AddIcon />} iconPosition="start" label={t('Create new')} />
        </Tabs>

        {/* Existing Job Tab */}
        <TabPanel value={tab} index={0}>
          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Quick selects: one click picks the job, no search needed */}
            {quickJobs?.length > 0 && (
              <Box data-testid="quick-select-jobs">
                <Typography
                  sx={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: 'text.secondary',
                    mb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <BoltIcon sx={{ fontSize: 16 }} />
                  {t('Quick selects')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {quickJobs.map((job) => {
                    const selected = selectedJob?.id === job.id;
                    return (
                      <Chip
                        key={job.id}
                        label={job.name}
                        clickable
                        onClick={() => handleJobChange(null, job)}
                        color={selected ? 'secondary' : 'default'}
                        variant={selected ? 'filled' : 'outlined'}
                        data-testid={`quick-select-job-${job.id}`}
                        sx={{ borderRadius: '8px', maxWidth: '100%' }}
                      />
                    );
                  })}
                </Box>
              </Box>
            )}
            {quickJobs?.length === 0 && canManageJobs && (
              <Typography sx={{ fontSize: '12px', color: 'text.secondary' }} data-testid="quick-select-jobs-hint">
                {t('quickSelectJobsEmptyHint')}
              </Typography>
            )}

            <DialogContentText sx={{ mb: 1 }}>
              {t('Select a job from your catalog')}
            </DialogContentText>
            <FormControl fullWidth>
              <Autocomplete
                name="job_id"
                // A quick select may not be among the current search results
                options={selectedJob && !jobs.some((j) => j.id === selectedJob.id) ? [selectedJob, ...jobs] : jobs}
                value={selectedJob}
                onChange={handleJobChange}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                {...jobsAutocompleteProps}
                getOptionLabel={(option) => {
                  if (!option) return '';
                  let label = option.name;
                  const extras = [];
                  if (option.job_code) extras.push(option.job_code);
                  const displayPrice = orgDefaultVat ? (option.price_incl_vat ?? option.price) : option.price;
                  if (displayPrice) extras.push(formatPriceWithCurrency(displayPrice));
                  if (option.category_name) extras.push(option.category_name);
                  if (extras.length > 0) {
                    label += ` (${extras.join(' | ')})`;
                  }
                  return label;
                }}
                renderOption={(props, option) => {
                  const displayPrice = orgDefaultVat ? (option.price_incl_vat ?? option.price) : option.price;
                  return (
                  <Box component="li" {...props} key={option.id}>
                    <Box sx={{ width: '100%' }}>
                      <Typography variant="body2" fontWeight={500}>
                        {option.name}
                        {option.job_code && (
                          <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                            [{option.job_code}]
                          </Typography>
                        )}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {[
                          displayPrice && `${formatPriceWithCurrency(displayPrice)} (${orgDefaultVat ? t('incl. VAT') : t('excl. VAT')})`,
                          option.price_type && t({fixed: 'Fixed', hourly: 'Hourly', variable: 'Variable'}[option.price_type] || option.price_type),
                          option.category_name,
                        ].filter(Boolean).join(' - ')}
                      </Typography>
                    </Box>
                  </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    autoComplete="off"
                    label={t('Job')}
                    placeholder={t('Search by name, code, category...')}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {jobsAutocompleteProps.loading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                      },
                    }}
                  />
                )}
              />
            </FormControl>

            {/* Quantity field */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                {selectedJob?.price_type === 'hourly' ? t('Hours') : t('Quantity')}
              </Typography>
              <DecimalField
                value={quantity}
                onChange={setQuantity}
                placeholder="1"
                size="small"
                inputProps={{ 'data-testid': 'add-job-quantity' }}
                sx={{
                  width: '100px',
                  '& .MuiOutlinedInput-root': { borderRadius: '10px' },
                }}
              />
            </Box>

            {/* Description field - shown after job is selected */}
            {selectedJob && (
              <Box sx={{ mt: 2 }}>
                <TextField
                  label={t('Description')}
                  value={existingJobDescription}
                  onChange={(e) => setExistingJobDescription(e.target.value)}
                  placeholder={t('Describe what work was done...')}
                  fullWidth
                  multiline
                  minRows={2}
                  maxRows={6}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': { borderRadius: '10px' },
                  }}
                />
                <Typography sx={{ fontSize: '12px', color: 'text.secondary', mt: 0.5 }}>
                  {t('Optional. Shows on invoice if filled.')}
                </Typography>
              </Box>
            )}

            {/* Assign mechanics */}
            {isMaintenanceMode && allEmployees.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
                  {t('Assigned mechanics')}
                </Typography>
                <Autocomplete
                  multiple
                  options={allEmployees}
                  getOptionLabel={(option) => option?.name || ''}
                  value={selectedEmployees}
                  onChange={(e, value) => setSelectedEmployees(value)}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Avatar
                        sx={{
                          width: 24,
                          height: 24,
                          fontSize: '11px',
                          bgcolor: 'primary.main',
                          mr: 1,
                        }}
                      >
                        {option.name?.split(' ').map(n => n[0]).join('').substring(0, 2)}
                      </Avatar>
                      <Typography sx={{ fontSize: '14px' }}>{option.name}</Typography>
                    </li>
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.id}
                        avatar={
                          <Avatar sx={{ bgcolor: 'primary.main', width: 22, height: 22, fontSize: '10px' }}>
                            {option.name?.split(' ').map(n => n[0]).join('').substring(0, 2)}
                          </Avatar>
                        }
                        label={option.name}
                        size="small"
                        sx={{ borderRadius: '8px' }}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      autoComplete="off"
                      placeholder={selectedEmployees.length === 0 ? t('Search mechanics...') : ''}
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                    />
                  )}
                />
              </Box>
            )}
          </Box>
        </TabPanel>

        {/* New Job Tab */}
        <TabPanel value={tab} index={1}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Name and Code row */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <TextField
                label={t('Job Name')}
                value={newJob.name}
                onChange={handleNewJobChange('name')}
                required
                fullWidth
                error={Boolean(error && !newJob.name.trim())}
                sx={{
                  flex: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                  },
                }}
              />
              <TextField
                label={t('Job Code')}
                value={newJob.job_code}
                onChange={handleNewJobChange('job_code')}
                placeholder="e.g., OIL-01"
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                  },
                }}
              />
            </Box>

            <TextField
              label={t('Description')}
              value={newJob.description}
              onChange={handleNewJobChange('description')}
              fullWidth
              multiline
              rows={2}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                },
              }}
            />

            {/* Category */}
            {showNewCategory ? (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  label={t('New Category Name')}
                  fullWidth
                  autoFocus
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '10px',
                    },
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleCreateCategory}
                  sx={{ borderRadius: '10px', px: 3 }}
                >
                  {t('Add')}
                </Button>
                <Button
                  onClick={() => {
                    setShowNewCategory(false);
                    setNewCategoryName('');
                  }}
                  sx={{ borderRadius: '10px' }}
                >
                  {t('Cancel')}
                </Button>
              </Box>
            ) : (
              <Autocomplete
                value={selectedCategory}
                onChange={handleCategoryChange}
                options={categories}
                getOptionLabel={(option) => option.name || ''}
                filterOptions={(options, params) => {
                  const filtered = options.filter((option) =>
                    option.name.toLowerCase().includes(params.inputValue.toLowerCase())
                  );

                  if (params.inputValue !== '' && !filtered.some((o) => o.name.toLowerCase() === params.inputValue.toLowerCase())) {
                    filtered.push({
                      inputValue: params.inputValue,
                      name: `${t('Add')} "${params.inputValue}"`,
                    });
                  }

                  return filtered;
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    autoComplete="off"
                    label={t('Category')}
                    placeholder={t('Select or create category...')}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                      },
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    {option.inputValue ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AddIcon fontSize="small" color="primary" />
                        <Typography>{option.name}</Typography>
                      </Box>
                    ) : (
                      option.name
                    )}
                  </li>
                )}
                freeSolo
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
              />
            )}

            {/* Price type and Price row */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <FormControl sx={{ minWidth: 140, flexShrink: 0 }}>
                <InputLabel>{t('Price Type')}</InputLabel>
                <Select
                  value={newJob.price_type}
                  onChange={handleNewJobChange('price_type')}
                  label={t('Price Type')}
                  sx={{ borderRadius: '10px' }}
                >
                  {priceTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {t(option.label)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DecimalField
                    label={
                      newJob.price_type === 'hourly'
                        ? (priceIncludesVat ? t('Hourly Rate (incl. VAT)') : t('Hourly Rate (excl. VAT)'))
                        : (priceIncludesVat ? t('Price (incl. VAT)') : t('Price (excl. VAT)'))
                    }
                    value={newJob.price}
                    disabled={!canEditPrices}
                    onChange={(value) => setNewJob({ ...newJob, price: value })}
                    allowNegative
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {newJob.price_type === 'hourly' ? `€/${t('h')}` : '€'}
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                      },
                    }}
                  />
                  <VatModeToggle
                    value={priceIncludesVat ? 'incl' : 'excl'}
                    onChange={(e, val) => val && setPriceIncludesVat(val === 'incl')}
                    sx={{ flexShrink: 0 }}
                  />
                </Box>
                {/* Show calculated price */}
                <VatConverseLine
                  value={newJob.price}
                  mode={priceIncludesVat ? 'incl' : 'excl'}
                  vatRate={newJob.vat_rate}
                  suffix={newJob.price_type === 'hourly' && `/${t('h')}`}
                />
              </Box>
            </Box>

            {/* Quantity field */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                {newJob.price_type === 'hourly' ? t('Hours') : t('Quantity')}
              </Typography>
              <DecimalField
                value={quantity}
                onChange={setQuantity}
                placeholder="1"
                size="small"
                sx={{
                  width: '100px',
                  '& .MuiOutlinedInput-root': { borderRadius: '10px' },
                }}
              />
            </Box>

            {/* Assign mechanics */}
            {isMaintenanceMode && allEmployees.length > 0 && (
              <Box>
                <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
                  {t('Assigned mechanics')}
                </Typography>
                <Autocomplete
                  multiple
                  options={allEmployees}
                  getOptionLabel={(option) => option?.name || ''}
                  value={selectedEmployees}
                  onChange={(e, value) => setSelectedEmployees(value)}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Avatar
                        sx={{
                          width: 24,
                          height: 24,
                          fontSize: '11px',
                          bgcolor: 'primary.main',
                          mr: 1,
                        }}
                      >
                        {option.name?.split(' ').map(n => n[0]).join('').substring(0, 2)}
                      </Avatar>
                      <Typography sx={{ fontSize: '14px' }}>{option.name}</Typography>
                    </li>
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.id}
                        avatar={
                          <Avatar sx={{ bgcolor: 'primary.main', width: 22, height: 22, fontSize: '10px' }}>
                            {option.name?.split(' ').map(n => n[0]).join('').substring(0, 2)}
                          </Avatar>
                        }
                        label={option.name}
                        size="small"
                        sx={{ borderRadius: '8px' }}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      autoComplete="off"
                      placeholder={selectedEmployees.length === 0 ? t('Search mechanics...') : ''}
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                    />
                  )}
                />
              </Box>
            )}
          </Box>

          {error && (
            <Typography color="error" sx={{ mt: 2, fontSize: '14px' }}>
              {error}
            </Typography>
          )}
        </TabPanel>
      </DialogContent>

      {/* Common Footer */}
      <Divider />
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        {/* Awaiting confirmation checkbox - only in maintenance mode */}
        {isMaintenanceMode && (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={awaitingConfirmation}
                onChange={(e) => setAwaitingConfirmation(e.target.checked)}
                sx={{ color: 'warning.main', '&.Mui-checked': { color: 'warning.main' } }}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HourglassEmptyIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                <Typography sx={{ fontSize: '14px' }}>
                  {t('Awaiting customer confirmation')}
                </Typography>
              </Box>
            }
          />
          <HelpButton text={t('awaitingConfirmationHelpJob')} />
        </Box>
        )}

        {/* Action buttons */}
        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: { xs: 'stretch', sm: 'flex-end' } }}>
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{ borderRadius: '10px', px: 3 }}
          >
            {t('Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={tab === 0 ? addExistingJob : createAndAddJob}
            disabled={tab === 0 ? !selectedJob || submitting : !isNewJobValid() || submitting}
            sx={{
              borderRadius: '10px',
              px: 3,
              bgcolor: 'secondary.main',
              '&:hover': { bgcolor: 'secondary.light' },
            }}
          >
            {submitting ? (
              <CircularProgress size={20} />
            ) : tab === 0 ? (
              t('Add')
            ) : (
              t('Create and add')
            )}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default withTranslation()(AddJobsDialog);
